import { useEffect, useState, useCallback } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { db } from "../firebase";
import ChaletCard from "./ChaletCard";
import { getChaletCover } from "../utils/chaletImages";
import "./home.css";

export default function Home({ chalets, setChalets }) {

  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [search, setSearch] = useState("");

  /*
  ========================================
  ترتيب الشاليهات
  ========================================
  */
  const getOrderValue = useCallback((item, fallbackIndex = 0) => {

    const rawOrder = item.order;

    if (typeof rawOrder === "number") return rawOrder;

    if (typeof rawOrder === "string" && rawOrder.trim() !== "") {

      const parsed = Number(rawOrder);

      if (!Number.isNaN(parsed)) return parsed;

    }

    if (item.createdAt?.toMillis) {

      try {
        return item.createdAt.toMillis();
      } catch {}

    }

    if (item.createdAt?.seconds) {

      return item.createdAt.seconds * 1000;

    }

    return fallbackIndex;

  }, []);


  const sortChalets = useCallback((items) => {

    return items
      .map((item, idx) => ({
        item,
        order: getOrderValue(item, idx)
      }))
      .sort((a, b) => a.order - b.order)
      .map(({ item }) => item);

  }, [getOrderValue]);


  /*
  ========================================
  ملاحظات افتراضية
  ========================================
  */
  const defaultNotes = [
    "تأمين (100) دينار كويتي",
    "الأسعار تختلف بالمناسبات والأعياد",
    "الدخول (10:00) صباحاً والخروج (02:00) ظهراً",
    "تضاف (50 د.ك) على سعر الحجز للدخول قبل الموعد",
    "يمنع إقامة الحفلات بجميع أنواعها",
    "يتم خصم مبلغ (50 د.ك) من التأمين في حال استلام الشاليه غير نظيف",
    "يتم خصم مبلغ (30 د.ك) من التأمين عن كل ساعة تأخير",
    "التأجير للعائلات فقط",
    "العربون غير مرجّع",
    "إرجاع التأمين خلال 24 ساعة",
    "آخر موعد دفع باقي المبلغ قبل الدخول بـ3 أيام",
    "يمنع الدخول بدون دفع كامل",
  ];


  /*
  ========================================
  تحميل الشاليهات
  ========================================
  */
  useEffect(() => {

    const q = query(
      collection(db, "chalets"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {

      const items = snap.docs
        .map(d => ({
          id: d.id,
          ...d.data()
        }))
        .filter(item => !item.hidden);

      setChalets(sortChalets(items));

    });

    return () => unsub();

  }, [setChalets, sortChalets]);


  /*
  ========================================
  تحميل المناسبات
  ========================================
  */
  useEffect(() => {

    const unsub = onSnapshot(
      collection(db, "events"),
      (snap) => {

        const list = snap.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(event => event.visible);

        setEvents(list);

      }
    );

    return () => unsub();

  }, []);


  const searchQuery = search.trim().toLowerCase();
  const visibleChalets = searchQuery
    ? chalets.filter((chalet) => String(chalet.name || "").toLowerCase().includes(searchQuery))
    : chalets;

  /*
  ========================================
  الواجهة
  ========================================
  */
  return (

    <div className="home">

      <div className="home-inner">

        <div className="home-search">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث عن شاليه..."
            aria-label="ابحث عن شاليه"
          />
        </div>

        {/* المناسبات */}
        {events.length > 0 && (

          <div className="events-section">

            {events.map(event => (

              <div
                key={event.id}
                className="event-card"
                onClick={() => navigate(`/event/${event.id}`)}
              >
                {event.name}
              </div>

            ))}

          </div>

        )}


        {/* الشاليهات */}
        <div className="grid">

          {visibleChalets.length === 0 && (
            <div className="home-search-empty">لا توجد نتائج</div>
          )}

          {visibleChalets.map((c, index) => {
            const cover = getChaletCover(c);
            return (

            <ChaletCard
              key={c.id}
              id={c.id}
              img={cover.desktop}
              imgMobile={cover.mobile}
              priority={index < 2}
              name={c.name}
              pricing={c.pricing}
              monthlyPrices={c.monthlyPrices}
              hasDiscount={c.hasDiscount}
              discountPercent={c.discountPercent}
              rooms={c.rooms}
              halls={c.halls}
              bathrooms={c.bathrooms}
              pool={c.pool}
              isPrivate={c.isPrivate}
              vip={c.vip}
              notes={
                Array.isArray(c.notes) && c.notes.length > 0
                  ? c.notes
                  : c.note
                  ? [c.note]
                  : defaultNotes
              }
              showNotes={false}
            />

          );
          })}

        </div>


      </div>

    </div>

  );

}
